import { useMemo } from 'react'

import { matchFilterArrayIncludes, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AgentRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['agents']

type AgentFilters = {
  exclude_user_ids?: number[],
  string?: string,
}

type UseAgentsOptions = {
  filters?: AgentFilters,
  performHttpRequests?: boolean,
  requestOptions?: AgentRequestOptions,
}

function useAgents(options: UseAgentsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    exclude_user_ids: filterExcludeUserIds,
    string: filterString,
  } = filters

  const {
    updatedEntities: { agents: agentsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { agents } = useSelector(reduxState => reduxState.entities)

  const filteredAgents = useMemo(() => {
    const filtered = Object.values(agents).filter((agent) => {
      const { full_name, id, user_id } = agent

      const matchExcludedUserIds = filterExcludeUserIds
        ? !matchFilterArrayIncludes(filterExcludeUserIds, user_id)
        : true

      const matchId = id.toString() === filterString
      const matchFullName = matchFilterString(full_name, filterString)
      const matchString = matchId || matchFullName

      return matchExcludedUserIds && matchString
    })

    return sortArrayBy(filtered, 'asc', 'full_name')
  }, [agentsUpdatedAt, JSON.stringify(filters)])

  const filteredAgentsCount = filteredAgents.length
  const hasFilteredAgents = !!filteredAgentsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAgentsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAgents } = useReduxAction(
    'agents',
    'loadAgents',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgents,
    filteredAgentsCount,
    hasFilteredAgents,
    loading: loadingAgents,
  }
}

export type useAgentsPayload = ReturnType<typeof useAgents>

export default useAgents
